import { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import getCurrentPage from './helpers/getCurrentPage'
import updateQuestionSectionsResponses from './helpers/updateQuestionSectionsResponses'
import { api } from 'services/api'
import { loggerInfo } from 'utils/infoLogs'
import { useHistory } from 'react-router-dom'
import { GlobalContext } from 'contexts/Global'

export const DISCARDED_SECTION_ERROR_CODE = 'DISCARDED_TRAUMATIC_EVENT_SECTION'

const useSurveyForm = () => {
	const refTimoutFalseLoading = useRef<any>(null)
	const [isLoading, setLoading] = useState(false)
	const [qtSections, setQtSections] = useState(0)
	const [currentPage, setCurrentPage] = useState<any>(null)
	const [initialPage, setInitialPage] = useState<number>(0)
	const [hasFilled, setFilled] = useState(false)
	const [formError, setFormError] = useState(false)
	const [isLoadingForm, setLoadingForm] = useState(false)
	const [questionSections, setQuestionSections] = useState<any>({})
	const [currentCampaignForm, setCurrentCampaignForm] = useState({
		id: null,
		questions: [],
		name: '',
		description: '',
	})
	const [questionAnswerModels, setQuestionAnswerModels] = useState([])
	const location = useLocation()
	const history = useHistory()
	const { companySurvey } = useContext<any>(GlobalContext)

	const queryString = new URLSearchParams(location.search)
	const queryId = queryString.get('id')
	const queryToken = queryString.get('token')

	const currentForm = typeof currentPage == 'number' ? currentPage : initialPage

	const onChangeUpdateQuestionsAnswer = (answer: any) => {
		if (answer.questionAnswerId) {
			const newQuestionSections = updateQuestionSectionsResponses(
				questionSections,
				currentCampaignForm,
				[answer],
			)

			setQuestionSections(newQuestionSections)
		}
	}

	const onScrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

	const isDiscardedSectionError = (error: any) =>
		Boolean(
			error?.response?.data?.errors?.some(
				(item: any) => item?.code === DISCARDED_SECTION_ERROR_CODE,
			),
		)

	const activeFalseLoading = () => {
		refTimoutFalseLoading.current && clearTimeout(refTimoutFalseLoading.current)
		setLoading(true)
		refTimoutFalseLoading.current = setTimeout(() => {
			setLoading(false)
		}, 1000)
		loggerInfo({
			data: { message: 'activeFalseLoading', response: {} },
			context: 'survey',
			location: 'activeFalseLoading',
			type: 'info',
		})
	}

	const onChangeFormSection = (type: 'next' | 'previous' | 'finished') => {
		activeFalseLoading()
		const changeSectionByType = {
			next: () => setCurrentPage(currentForm + 1),
			previous: () => setCurrentPage(currentForm - 1),
			finished: () => setCurrentPage(qtSections - 1),
		}

		const changeSection = changeSectionByType[type]
		const hasFilled = type === 'finished' ? true : false

		changeSection()
		setFilled(hasFilled)
		onScrollToTop()
		loggerInfo({
			data: { message: 'onScrollToTop', response: {} },
			context: 'survey',
			location: 'onChangeFormSection',
			type: 'info',
		})
	}

	const getChangeSectionType = (hasFinished: boolean) => {
		loggerInfo({
			data: { message: 'onScrollToTop', response: {} },
			context: 'survey',
			location: 'changeSectionType',
			type: 'info',
		})
		const hasAfterLastSection = currentForm + 1 === qtSections

		if (hasFinished || hasAfterLastSection) {
			return 'finished'
		}

		return 'next'
	}

	const onFinish = async (questionsResponse: any) => {
		setFormError(false)
		setLoadingForm(true)

		try {
			const filledAllInputs = questionsResponse.every(
				(response: any) => response.questionAnswerId,
			)
			loggerInfo({
				data: { data: filledAllInputs },
				context: 'survey',
				location: 'filledAllInputs',
				type: 'info',
			})

			if (!filledAllInputs) {
				setFormError(true)
				return null
			}

			const formattedQuestionsResponse = questionsResponse.map(
				(question: any) => ({
					id: question.id,
					questionAnswerId:
						question.questionAnswerId === 'empty'
							? null
							: question.questionAnswerId,
				}),
			)

			const { data: responsePut }: any = await api.put(
				`/campaign-answers/${queryId}/sections/${currentCampaignForm.id}`,
				{ answers: formattedQuestionsResponse },
			)

			if (questionsResponse.length) {
				const newQuestionSections = updateQuestionSectionsResponses(
					questionSections,
					currentCampaignForm,
					questionsResponse,
				)

				setQuestionSections(newQuestionSections)
			}

			const changeSectionType = getChangeSectionType(
				responsePut?.content?.finished,
			)

			changeSectionType && onChangeFormSection(changeSectionType)
		} catch (error: any) {
			loggerInfo({
				data: error,
				context: 'survey',
				location: 'onFinish',
			})

			if (isDiscardedSectionError(error)) {
				setFilled(true)
				onScrollToTop()
				return null
			}

			setFormError(false)
		} finally {
			setLoadingForm(false)
		}
	}

	const handleBackToList = () => {
		const hasFirstSection = currentForm > 0

		if (hasFirstSection) {
			onChangeFormSection('previous')
		} else {
			history.push({
				pathname: '/cuestionario/nom-035/list',
				search: `?token=${queryToken}`,
			})
		}
	}

	const barPorcent = ((currentForm + 1) / qtSections) * 100

	const fetchForm = async () => {
		setLoading(true)
		try {
			const { data } = await api.get(`/campaign-answers/${queryId}`)

			const campaignAnswerSession = data?.content?.campaignAnswerSession
			const questionSectionsData =
				campaignAnswerSession?.questionnaire?.questionSections

			const isSessionFinished = Boolean(
				data?.content?.finished ||
					campaignAnswerSession?.finished ||
					campaignAnswerSession?.dateCompleted,
			)

			if (isSessionFinished) {
				setFilled(true)
			} else if (currentPage === null) {
				setInitialPage(
					getCurrentPage(questionSectionsData, () => setFilled(true)),
				)
			}

			setQtSections(questionSectionsData.length)

			setQuestionSections(questionSectionsData)

			setCurrentCampaignForm(
				questionSectionsData[currentForm] || { questions: [] },
			)

			setQuestionAnswerModels(data.content?.questionAnswerModels)
		} catch (error) {
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const getCurrentCampaignForm = () => {
		setCurrentCampaignForm(questionSections[currentForm] || { questions: [] })
	}

	const getHasSessionCompleted = () => {
		const session = companySurvey?.campaignAnswerSessions?.find(
			(campaignAnswerSession: any) => campaignAnswerSession.id === queryId,
		)

		if (session?.dateCompleted) {
			setFilled(true)
		}
	}

	useEffect(() => {
		fetchForm()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		getHasSessionCompleted()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companySurvey, queryId])

	useEffect(() => {
		getCurrentCampaignForm()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentForm, questionSections])

	return {
		currentForm,
		qtSections,
		onFinish,
		handleBackToList,
		currentCampaignForm,
		queryId,
		barPorcent,
		questionAnswerModels,
		isLoading,
		hasFilled,
		formError,
		isLoadingForm,
		setFormError,
		onChangeUpdateQuestionsAnswer,
	}
}

export default useSurveyForm
